import {
    IPatientIdentificationData,
    IPolicyIdentificationData,
} from "@common-shared/types/policies.types.ts"
import {
    IOneVerifyCreatePolicyCommentRequestData,
    IOneVerifyGetPolicyCommentsListRequestData,
    IOneVerifyUpdatePolicyCommentRequestData,
} from "@common-shared/types/requestData/oneVerifyRequestData.types.ts"
import type { IPractice } from "@common-shared/types/responseData/commonResponseData.types.ts"
import { IOneVerifyPolicyComment } from "@common-shared/types/responseData/oneVerifyResponseData.types.ts"

import { API_ROUTES } from "../config/routes.config"
import { HttpHelper } from "../helpers/http.helper"
import { UtilHelper } from "../helpers/util.helper.tsx"

export class CoverageHistoryCommentsService {
    /**
     * Get list of comments associated with patient or policy
     */
    static async getList(
        practice: IPractice,
        patient?: IPatientIdentificationData,
        policy?: IPolicyIdentificationData,
    ): Promise<IOneVerifyPolicyComment[] | undefined> {
        const requestData: IOneVerifyGetPolicyCommentsListRequestData = {}

        if (patient) {
            // Because GET
            // @ts-ignore
            requestData.patient = JSON.stringify(patient)
        }

        if (policy) {
            // Because GET
            // @ts-ignore
            requestData.policy = JSON.stringify(policy)
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.COMMENTS_GET_LIST.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            requestData,
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Create policy comment
     */
    static async create(
        practice: IPractice,
        comment: string,
        policy: IPolicyIdentificationData,
        patient?: IPatientIdentificationData,
        replyToCommentId?: string,
    ): Promise<IOneVerifyPolicyComment | undefined> {
        const requestData: IOneVerifyCreatePolicyCommentRequestData = {
            comment,
            policy,
            patient,
            replyToCommentId,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.COMMENTS_CREATE.replace(
                ":practiceId",
                practice.id.toString(),
            ),
            requestData,
            "POST",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Update policy comment
     */
    static async update(
        practice: IPractice,
        commentId: string,
        comment: string,
    ): Promise<IOneVerifyPolicyComment | undefined> {
        const requestData: IOneVerifyUpdatePolicyCommentRequestData = {
            comment,
        }

        const result = await HttpHelper.sendRequest(
            API_ROUTES.COMMENTS_UPDATE_SINGLE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":commentId", commentId),
            requestData,
            "PUT",
        )

        return result && !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }

    /**
     * Delete policy comment
     */
    static async delete(
        practice: IPractice,
        commentId: string,
    ): Promise<
        | {
              deletedId: string
          }
        | undefined
    > {
        const result = await HttpHelper.sendRequest(
            API_ROUTES.COMMENTS_DELETE_SINGLE.replace(
                ":practiceId",
                practice.id.toString(),
            ).replace(":commentId", commentId.toString()),
            undefined,
            "DELETE",
        )

        return !UtilHelper.isEmptyObject(result) && !result.errorData
            ? result
            : undefined
    }
}
